import { useCallback, useEffect, useState } from 'react';
import { api } from './api';
import type { Assignment, Customer, LogEntry, NewBizProspect, Quote, User } from './types';

export interface AppState {
  user: User | null;
  customers: Customer[];
  logs: LogEntry[];
  users: User[];
  assignments: Assignment[];
  newbiz: NewBizProspect[];
  quotes: Quote[];
}

export interface AppActions {
  refreshAll: () => Promise<void>;
  refreshPipeline: () => Promise<void>;
  setUser: (u: User | null) => void;
  addCustomerLocal: (c: Customer) => void;
  removeCustomerLocal: (id: string) => void;
  addLogLocal: (l: LogEntry) => void;
  updateLogLocal: (l: LogEntry) => void;
  removeLogLocal: (id: string) => void;
  addUserLocal: (u: User) => void;
  removeUserLocal: (id: string) => void;
  upsertAssignmentLocal: (a: Assignment) => void;
  removeAssignmentLocal: (id: string) => void;
  upsertNewBizLocal: (n: NewBizProspect) => void;
  removeNewBizLocal: (id: string) => void;
  upsertQuoteLocal: (q: Quote) => void;
  removeQuoteLocal: (id: string) => void;
}

const EMPTY: AppState = {
  user: null, customers: [], logs: [], users: [], assignments: [], newbiz: [], quotes: []
};

export function useAppStore() {
  const [state, setState] = useState<AppState>(EMPTY);
  const [loading, setLoading] = useState(true);

  const refreshAll = useCallback(async () => {
    const me = await api.me();
    if (!me.user) {
      setState(EMPTY);
      return;
    }
    const [customers, logs, users, assignments, newbiz, quotes] = await Promise.all([
      api.listCustomers(),
      api.listLogs(),
      me.user.role === 'admin' ? api.listUsers() : Promise.resolve<User[]>([]),
      api.listAssignments(),
      api.listNewBiz(),
      api.listQuotes()
    ]);
    setState({ user: me.user, customers, logs, users, assignments, newbiz, quotes });
  }, []);

  const refreshPipeline = useCallback(async () => {
    const [assignments, newbiz] = await Promise.all([api.listAssignments(), api.listNewBiz()]);
    setState((s) => ({ ...s, assignments, newbiz }));
  }, []);

  useEffect(() => {
    (async () => {
      try { await refreshAll(); } finally { setLoading(false); }
    })();
  }, [refreshAll]);

  const upsertById = <T extends { id: string }>(arr: T[], item: T): T[] => {
    const i = arr.findIndex(x => x.id === item.id);
    if (i >= 0) { const next = arr.slice(); next[i] = item; return next; }
    return [item, ...arr];
  };

  const actions: AppActions = {
    refreshAll, refreshPipeline,
    setUser: (u) => setState((s) => ({ ...s, user: u })),
    addCustomerLocal: (c) => setState((s) => ({ ...s, customers: [...s.customers, c].sort((a, b) => a.name.localeCompare(b.name)) })),
    removeCustomerLocal: (id) => setState((s) => ({ ...s, customers: s.customers.filter(c => c.id !== id), logs: s.logs.filter(l => l.customerId !== id) })),
    addLogLocal: (l) => setState((s) => ({ ...s, logs: [l, ...s.logs] })),
    updateLogLocal: (l) => setState((s) => ({ ...s, logs: s.logs.map(x => x.id === l.id ? l : x) })),
    removeLogLocal: (id) => setState((s) => ({ ...s, logs: s.logs.filter(l => l.id !== id) })),
    addUserLocal: (u) => setState((s) => ({ ...s, users: [...s.users, u].sort((a, b) => a.name.localeCompare(b.name)) })),
    removeUserLocal: (id) => setState((s) => ({ ...s, users: s.users.filter(u => u.id !== id) })),
    upsertAssignmentLocal: (a) => setState((s) => ({ ...s, assignments: upsertById(s.assignments, a) })),
    removeAssignmentLocal: (id) => setState((s) => ({ ...s, assignments: s.assignments.filter(a => a.id !== id) })),
    upsertNewBizLocal: (n) => setState((s) => ({ ...s, newbiz: upsertById(s.newbiz, n) })),
    removeNewBizLocal: (id) => setState((s) => ({ ...s, newbiz: s.newbiz.filter(n => n.id !== id) })),
    upsertQuoteLocal: (q) => setState((s) => ({ ...s, quotes: upsertById(s.quotes, q) })),
    removeQuoteLocal: (id) => setState((s) => ({ ...s, quotes: s.quotes.filter(q => q.id !== id) }))
  };

  return { state, actions, loading };
}
